import { Component, Inject, computed, inject, signal } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';

import { RolesService } from '@shared/services/entities/roles.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import {
  Role,
  CreateRolePayload,
  UpdateRolePayload,
  AccessPermissionsMap,
} from '@shared/models/entities/roles.model';
import { ModuleModel } from '@shared/models/entities/module.model';
import { Permission } from '@shared/models/entities/permission.model';

export interface RoleFormDialogData {
  /** Rol a editar; ausente = crear. */
  role?: Role;
  /** Catálogos (los carga la pantalla padre). */
  modules: ModuleModel[];
  permissions: Permission[];
}

/**
 * Diálogo de alta/edición de rol, sobre el cascarón compartido
 * (`FormDialogShellComponent`). El acordeón de módulos/permisos era el caso
 * más doloroso del panel inline: con muchos módulos había que scrollear toda
 * la página para llegar a Guardar. Aquí el título y las acciones quedan
 * fijos y solo la matriz scrollea.
 */
@Component({
  selector: 'app-role-form-dialog',
  standalone: true,
  imports: [ MatButtonToggleModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDividerModule,
    MatExpansionModule,
    FormDialogShellComponent,
  ],
  templateUrl: './role-form-dialog.component.html',
  styleUrl: './role-form-dialog.component.scss',
})
export class RoleFormDialogComponent {
  private readonly fb                   = inject(FormBuilder);
  private readonly rolesService         = inject(RolesService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialogRef            = inject(MatDialogRef<RoleFormDialogComponent>);

  readonly isEditing: boolean;
  readonly isCreating: boolean;
  availableModules:     ModuleModel[] = [];
  availablePermissions: Permission[]  = [];

  saving = signal(false);

  // Estado de selección en signals para que el template reaccione.
  selectedModules     = signal<string[]>([]);
  selectedPermissions = signal<AccessPermissionsMap>({});

  /** Buscador rápido de módulos (texto + asignados / sin asignar). */
  moduleQuery = signal('');
  moduleScope = signal<'all' | 'on' | 'off'>('all');

  private static norm(v: unknown): string {
    return String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  readonly filteredModules = computed<ModuleModel[]>(() => {
    const q = RoleFormDialogComponent.norm(this.moduleQuery());
    const scope = this.moduleScope();
    const selected = this.selectedModules();
    return this.availableModules.filter(m => {
      const on = selected.includes(m.id!);
      if (scope === 'on' && !on) return false;
      if (scope === 'off' && on) return false;
      if (!q) return true;
      const hay = RoleFormDialogComponent.norm([m.label, m.name, (m as any).route, (m as any).description].join(' '));
      return q.split(/\s+/).every(t => hay.includes(t));
    });
  });

  /** Enter en el buscador: activa (y abre) el primer módulo encontrado. */
  activateFirstMatch(): void {
    const first = this.filteredModules()[0];
    if (first?.id && !this.isModuleSelected(first.id)) this.toggleModule(first.id, true);
  }

  form: FormGroup = this.fb.group({
    name:        ['', [Validators.required, Validators.minLength(2)]],
    description: ['Rol sin descripción'],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: RoleFormDialogData) {
    this.isEditing = !!data?.role;
    this.isCreating = !this.isEditing;
    this.availableModules     = data?.modules ?? [];
    this.availablePermissions = data?.permissions ?? [];

    const role = data?.role;
    if (role) {
      this.form.patchValue({ name: role.name, description: role.description });
      this.selectedModules.set([...(role.navAccessTree ?? [])]);
      // Clon defensivo: no mutar el rol cargado de la tabla.
      const cloned: AccessPermissionsMap = {};
      const original = role.accessPermissions ?? {};
      for (const key of Object.keys(original)) {
        cloned[key] = [...(original[key] ?? [])];
      }
      this.selectedPermissions.set(cloned);
    }
  }

  // ─── Selección de módulos y permisos ────────────────────────────────────────

  isModuleSelected(moduleId: string): boolean {
    return this.selectedModules().includes(moduleId);
  }

  /** Marca/desmarca un módulo. Al desmarcar, borra sus permisos asignados. */
  toggleModule(moduleId: string, checked: boolean): void {
    if (checked) {
      this.selectedModules.update(list => [...list, moduleId]);
      this.selectedPermissions.update(map => map[moduleId] ? map : { ...map, [moduleId]: [] });
    } else {
      this.selectedModules.update(list => list.filter(id => id !== moduleId));
      this.selectedPermissions.update(map => {
        const next = { ...map };
        delete next[moduleId];
        return next;
      });
    }
  }

  isPermissionSelected(moduleId: string, permissionId: string): boolean {
    return (this.selectedPermissions()[moduleId] ?? []).includes(permissionId);
  }

  togglePermission(moduleId: string, permissionId: string, checked: boolean): void {
    this.selectedPermissions.update(map => {
      const current = map[moduleId] ?? [];
      const next = checked
        ? [...current, permissionId]
        : current.filter(id => id !== permissionId);
      return { ...map, [moduleId]: next };
    });
  }

  selectAllPermissionsFor(moduleId: string): void {
    const allIds = this.availablePermissions
      .map(p => p.id)
      .filter((id): id is string => !!id);
    this.selectedPermissions.update(map => ({ ...map, [moduleId]: allIds }));
  }

  clearPermissionsFor(moduleId: string): void {
    this.selectedPermissions.update(map => ({ ...map, [moduleId]: [] }));
  }

  permissionsCountFor(moduleId: string): number {
    return (this.selectedPermissions()[moduleId] ?? []).length;
  }

  // ─── Guardar / cerrar ───────────────────────────────────────────────────────

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, description } = this.form.value;

    // Sanitizar accessPermissions: solo entradas de módulos seleccionados.
    const navAccessTree = [...this.selectedModules()];
    const cleanPermissions: AccessPermissionsMap = {};
    const allPerms = this.selectedPermissions();
    for (const moduleId of navAccessTree) {
      cleanPermissions[moduleId] = allPerms[moduleId] ?? [];
    }

    this.saving.set(true);
    try {
      if (this.isCreating) {
        const payload: CreateRolePayload = {
          name,
          description,
          navAccessTree,
          accessPermissions: cleanPermissions,
          tools: [],
        };
        const created = await this.rolesService.createRole(payload);
        await this.customMessageService.open({
          title: '¡Creado!', type: 'success',
          message: `Rol <b>${created?.name ?? name}</b> creado correctamente.`,
          duration: 2500,
        });
      } else {
        const payload: UpdateRolePayload = {
          id: this.data.role!.id,
          name,
          description,
          navAccessTree,
          accessPermissions: cleanPermissions,
        };
        const updated = await this.rolesService.updateRole(payload);
        await this.customMessageService.open({
          title: '¡Actualizado!', type: 'success',
          message: `Rol <b>${updated?.name ?? name}</b> actualizado correctamente.`,
          duration: 2500,
        });
      }
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ocurrió un error al guardar el rol.';
      await this.customMessageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    } finally {
      this.saving.set(false);
    }
  }
}
